// components/auth/LastNamePromptDialog.tsx
//
// One-time blocking prompt that fires for any staff / admin account
// whose /users/{uid} record is missing a lastName. Existing accounts
// were registered before the registration form started asking for a
// surname, so they render bare 'Firstname' across staff-facing UIs.
// This dialog backfills the field so every display can lean on
// obscureName() and render 'Firstname L.' uniformly.
//
// Mounted inside ProtectedRoute so it appears on the first
// authenticated render and re-checks after the user dismisses it.
// Players, parents, children, and store accounts are not prompted —
// player-side displays don't use surnames and store accounts have
// no personal surname expected.

import { ReactElement, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { ref, update } from 'firebase/database';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../utils/logger';

export function LastNamePromptDialog(): ReactElement | null {
  const { user } = useAuth();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;
  // Only prompt approved staff / admin records. Players, parents,
  // children, and stores don't render surnames on staff displays,
  // and unapproved staff get bounced before they hit anything that
  // would render their name anyway.
  const accountType = user.accountType;
  const needsPrompt =
    user.approved === true &&
    (accountType === 'staff' || accountType === 'admin') &&
    !user.lastName &&
    !!user.firstName;
  if (!needsPrompt) return null;

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError('Last name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await update(ref(db, `users/${user.uid}`), { lastName: trimmed });
      // AuthContext's onValue subscription on /users/{uid} will pick
      // up the new field on the next snapshot and re-render us out
      // of view automatically — no local state flip needed.
    } catch (err) {
      logger.error('Last name save failed:', err);
      setError(
        err instanceof Error
          ? `Couldn't save: ${err.message}`
          : "Couldn't save the last name. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      // No onClose — backdrop click and ESC should not let the
      // operator skip this. They unblock by filling the field.
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ color: '#FFF' }}>One more thing — your last name</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#8892A0', mb: 2 }}>
          Staff displays now render as{' '}
          <Box component="span" sx={{ color: '#00FFD4', fontWeight: 600 }}>
            {user.firstName} L.
          </Box>{' '}
          (first name + last initial) so the floor can tell two people
          with the same first name apart. We don't have your last name
          on file yet — drop it in to continue.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Last name"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !saving) {
              e.preventDefault();
              handleSave();
            }
          }}
          disabled={saving}
          autoComplete="family-name"
          inputProps={{ maxLength: 60 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#252D3A',
              borderRadius: '8px',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
              '&:hover fieldset': { borderColor: '#00FFD4' },
              '&.Mui-focused fieldset': { borderColor: '#00FFD4' },
            },
            '& .MuiInputLabel-root': { color: '#8892A0' },
            '& .MuiOutlinedInput-input': { color: '#FFF' },
          }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 1.5 }}>
          Only the first initial is shown publicly (e.g. <em>{user.firstName} S.</em>). Admins see the full surname on internal screens.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSave}
          disabled={saving || !value.trim()}
          variant="contained"
          sx={{
            bgcolor: '#00FFD4',
            color: '#000',
            fontWeight: 700,
            '&:hover': { bgcolor: '#00D4B0' },
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LastNamePromptDialog;
